import { useCallback } from 'react';
import styled from 'styled-components';

import { THIN_SCROLLBAR } from '../../styles/scrollbar';

import { useAgentActions, useAgentState } from '../../providers/contexts';
import { useTabsActions } from '../../providers/TabsProvider';

interface Example {
  readonly prompt: string;
  readonly hint: string;
  /** Short user-facing summary of what the prompt asks for. Used by the
   *  empty-state chips in ChatView. */
  readonly summary: string;
}

interface Group {
  readonly title: string;
  readonly examples: readonly Example[];
}

// One example per entry in the agent component registry. Each prompt is
// phrased so the agent picks that specific component; the hint names the
// registry id so users can map the prompt → render.
export const GROUPS: readonly Group[] = [
  {
    title: 'Compare markets',
    examples: [
      {
        prompt:
          'Compare BTC / ETH / SOL current funding rates and 24h volume in a data_table.',
        hint: 'Renders a side-by-side table — pick any markets and columns; useful for quick funding or liquidity checks.',
        summary: 'Compare BTC / ETH / SOL funding and volume',
      },
    ],
  },
  {
    title: 'Price chart',
    examples: [
      {
        prompt:
          'Plot the BTC-USD-PERP price for the last 24 hours as a performance_chart.',
        hint: 'Area chart over a time range — works for prices, PnL curves, or any series. Swap the symbol and window to fit.',
        summary: 'Plot the BTC-USD-PERP price for the last 24 hours',
      },
    ],
  },
  {
    title: 'Explanation',
    examples: [
      {
        prompt:
          'Explain in markdown how Paradex perpetual funding works in 3 short paragraphs.',
        hint: 'Freeform prose — useful when you want an explainer rather than data. Headers, lists, and code blocks all render.',
        summary: 'Explain how Paradex perpetual funding works',
      },
    ],
  },
  {
    title: 'My positions',
    examples: [
      {
        prompt: 'List my open positions in a positions_table.',
        hint: 'Pulls your live positions — size, entry, mark, liquidation, PnL, and leverage — into a typed table.',
        summary: 'List my open positions',
      },
    ],
  },
  {
    title: 'Account value',
    examples: [
      {
        prompt:
          "What's my current account value? Render it as a single metric_card.",
        hint: 'Single KPI tile — one label and one big number, with an optional up/down tint. Good for any "current" headline metric.',
        summary: 'Show my current account value',
      },
    ],
  },
  {
    title: 'Risk alert',
    examples: [
      {
        prompt:
          'Render an alert_banner (variant=error) telling me my margin is critical.',
        hint: 'Colored callout banner with severity — error, warning, or info. Use it whenever the agent needs to flag a state change.',
        summary: 'Warn me that my margin is critical',
      },
    ],
  },
  {
    title: 'Quick lookup',
    examples: [
      {
        prompt:
          'Show my ETH-USD-PERP entry price and current mark price as labeled_output rows.',
        hint: 'Compact label-and-value pairs, one per row. Good for cherry-picking a few related numbers without a full table.',
        summary: 'Show my ETH entry vs mark price',
      },
    ],
  },
];

export default function ExamplesView() {
  const agentActions = useAgentActions();
  const agentState = useAgentState();
  const { setActiveTab } = useTabsActions();

  const isReady = agentState.status === 'authenticated';

  const tryPrompt = useCallback(
    (prompt: string) => {
      if (!isReady) return;
      agentActions.sendMessage(prompt);
      setActiveTab('chat');
    },
    [isReady, agentActions, setActiveTab],
  );

  return (
    <Scroll>
      <Intro>
        <h3>Examples</h3>
        <p>
          Try a prompt to see how the agent responds. Click <em>Run</em> to send
          it.
        </p>
        {!isReady && (
          <NotReady>
            Connect to the agent first — the Run buttons activate once the
            connection badge turns green.
          </NotReady>
        )}
      </Intro>
      {GROUPS.map((group) => (
        <Group key={group.title}>
          <GroupTitle>{group.title}</GroupTitle>
          {group.examples.map((ex) => (
            <Card key={ex.prompt}>
              <Prompt>{ex.prompt}</Prompt>
              <Hint>{ex.hint}</Hint>
              <RunButton
                type="button"
                disabled={!isReady}
                onClick={() => tryPrompt(ex.prompt)}
                title={isReady ? 'Send to the agent' : 'Not connected'}
              >
                Run
              </RunButton>
            </Card>
          ))}
        </Group>
      ))}
      <TipCallout role="note">
        <TipLabel>Tip</TipLabel>
        The agent keeps your operator address in session context, so phrases
        like &quot;my&quot;, &quot;I&quot;, and &quot;show me my X&quot; already
        mean your account.
      </TipCallout>
    </Scroll>
  );
}

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 920px;
  width: 100%;
  margin: 0 auto;
  ${THIN_SCROLLBAR}
`;

const Intro = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);

  h3 {
    margin: 0 0 6px 0;
    font-size: 16px;
    font-weight: 500;
    color: var(--text-primary);
  }

  p {
    margin: 0;
    line-height: 1.5;
  }
`;

const NotReady = styled.div`
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: color-mix(
    in srgb,
    var(--warning-color, #ffcb23) 10%,
    transparent
  );
  border: 1px solid
    color-mix(in srgb, var(--warning-color, #ffcb23) 30%, transparent);
  color: var(--text-primary);
  font-size: 0.75rem;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupTitle = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: var(--text-secondary);
`;

const Card = styled.div`
  position: relative;
  padding: 10px 12px 12px;
  border-radius: 7px;
  background: var(--bg-color-container);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Prompt = styled.div`
  font-size: 0.8125rem;
  color: var(--text-primary);
  line-height: 1.4;
  padding-right: 56px;
`;

const Hint = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  line-height: 1.4;
  padding-right: 56px;
`;

const RunButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 999px;
  border: none;
  /* Match the All/Installed pill: dark-grey resting fill, lighter (not
     darker) on hover, so the button reads as activating rather than
     pressing in. */
  background: var(--button-color-dark);
  color: var(--text-primary);
  transition: background 0.15s ease;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--button-color);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

// Callout for the "Tip:" line at the bottom of the Examples list. The faint
// left-rail and tinted background mark it as advisory copy rather than just
// another paragraph, without competing visually with the cards above.
const TipCallout = styled.div`
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: 7px;
  background: color-mix(in srgb, var(--accent-color) 8%, transparent);
  font-size: 0.75rem;
  color: var(--text-primary);
  line-height: 1.5;
`;

const TipLabel = styled.span`
  font-weight: 600;
  color: var(--accent-color);
  margin-right: 6px;
`;
